import { useState } from "react";
import React from "react";
import { doFetch } from "lib/doFetch";
import { useProdCopyUrl } from "components/PreferencesDialog/redesignUtils";
import useSubscriptions from "lib/hooks/useSubscriptions/useSubscriptions";
import { useCreateSubscriptionsRequestBody } from "components/Subscriptions/SubscriptionsModal/SubscriptionsModal.helpers";
import { useToast } from "lib/toast/ToastContext";
import { _t } from "../../../app/i18n/i18n";
import { auditOnSubscription, auditLocationChange } from "../../analytics/auditEvents";

const jsApiUrl = (prodCopyUrl) => `/research/${prodCopyUrl}/subscription/batch`;

function parseErrorDetails(errMessage) {
  const statusMatch = errMessage.match(/bad response:\s*(\d+)/);
  const statusCode = statusMatch ? Number.parseInt(statusMatch[1], 10) : 0;

  let apiMessage = "";
  const bracketMatch = errMessage.match(/\[(.*)]/s);
  if (bracketMatch) {
    try {
      const parsed = JSON.parse("[" + bracketMatch[1] + "]");
      apiMessage = Array.isArray(parsed) ? parsed.join(", ") : String(parsed);
    } catch {
      apiMessage = bracketMatch[1].replaceAll('"', "");
    }
  }

  return { statusCode, apiMessage };
}

function getErrorHeadingByStatus(statusCode) {
  switch (statusCode) {
    case 400:
      return _t("Invalid Subscription Request");
    case 409:
      return _t("Subscription Already Exists");
    case 500:
      return _t("Service Error");
    default:
      return _t("Subscription Failed");
  }
}

function getErrorMessage(statusCode, apiMessage) {
  switch (statusCode) {
    case 400:
      return _t("The request was invalid. Please try again.");
    case 404:
      return `${apiMessage} ${_t("is either inactive or not available for subscriptions.")}`;
    case 409:
      return `${_t("You are already subscribed to the following")}: ${apiMessage}`;
    case 500:
      return _t("Something went wrong. Please try again later.");
    default:
      return _t("An unexpected error occurred. Please try again.");
  }
}

function getSelectedSubscriptionNames(selectedSubscriptionTags) {
  return selectedSubscriptionTags
    ?.filter(item => item?.selected === true && item?.tag)
    ?.map(item => item.tag) || [];
}

export function useOnSubscribe() {
  const {
    selectedSubscriptionTags,
    selectedLanguages,
    setModalShow,
    selectedPub,
    isSubscriptionFromPillsBar
  } = useSubscriptions();

  const toast = useToast();
  const prodCopyUrl = useProdCopyUrl();
  const createRequestBody = useCreateSubscriptionsRequestBody;
  const [loading, setLoading] = useState(false);

  const handleMySubscriptionsClick = (e) => {
    e.preventDefault();
    auditLocationChange(null, { payload: { location: { pathname: "/MySubscriptions" } } });
    window.open("/research/japi/subscription_handshake", "_blank");
  };

  async function onSubscribe(validatedTags) {
    setLoading(true);
    setModalShow(false);
    const loadingToast = toast.loading("Subscribing...", { heading: "Subscription" });

    // Use validated tags if provided, otherwise fall back to selectedSubscriptionTags
    const tagsToUse = validatedTags || selectedSubscriptionTags;
    const selectedTags = getSelectedSubscriptionNames(tagsToUse);
    const requestBody = createRequestBody(
      tagsToUse,
      selectedLanguages,
      selectedPub,
      isSubscriptionFromPillsBar,
    );

    try {
      const response = await doFetch(jsApiUrl(prodCopyUrl), JSON.stringify(requestBody), "POST");

      loadingToast.hide();

      const fullSentence = _t("The newly added subscription can be managed via My Subscriptions.");
      const mySubscriptionsText = _t("My Subscriptions");

      const parts = fullSentence.split(mySubscriptionsText);

      const subHeadingWithLink = (
        <span>
          {parts[0]}
          <a
            href="/research/japi/subscription_handshake"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleMySubscriptionsClick}
            style={{
              color: '#007bff',
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
          >
            {mySubscriptionsText}
          </a>
          {parts[1]}
        </span>
      );

      if (Array.isArray(response) && response?.length) {
        const successMsg = response.filter(Boolean).join(". ");
        toast.success(_t("You will start receiving email alerts based on your subscriptions."), {
          heading: _t("Subscription Successful"),
          subHeading: subHeadingWithLink,
          tags: selectedTags,
          autoClose: 8000
        });
      } else {
        toast.info(_t("You will start receiving email alerts based on your subscriptions."), {
          heading: _t("Subscription Successful"),
          subHeading: subHeadingWithLink,
          tags: selectedTags,
          autoClose: 8000
        });
      }

      try {
        const subscriptionName = response[0]?.split("name: ")[1]?.trim() || response[0];
        auditOnSubscription(subscriptionName);
      } catch (_) {}
    } catch (err) {
      loadingToast.hide();

      const { statusCode, apiMessage } = parseErrorDetails(err.message || "");
      const heading = getErrorHeadingByStatus(statusCode);
      const message = getErrorMessage(statusCode, apiMessage);

      if (statusCode === 409) {
        toast.warn(message, { heading });
      } else {
        toast.error(message, { heading });
        auditOnSubscription(message, "subscription-failed");
      }
    } finally {
      setLoading(false);
    }
  }

  return { onSubscribe };
}